import * as React from "react";
import {useState} from "react";
import {MenuItem, Select} from "@mui/material";
import {useDispatch} from "react-redux";
import {showNotification} from "../../../actions";
import {defineMessage} from "react-intl";
import {FormattedFormRow, FormContainer, DialogContent, FormActionButtons} from "@dvrt/graphql-widgets";
import DataSource from "./DataSource";
import {useDeleteLocationMutation, useMoveExtensionsAndDeleteLocationMutation} from "../queries.generated";
import {FormattedPageTitle} from "@/components/pages";
import {commonTranslations} from "@/utils/translations";
import {useNavigator} from "@/hooks";

type Props = {
    locationId: number;
};

type LocationOptions = {
    [key: number]: string;
};

export default ({locationId}: Props) => {
    const [submitting, setSubmitting] = useState(false);
    const [destinationLocationId, setDestinationLocationId] = useState(0);
    const dataSource = new DataSource(locationId);
    const [options, setOptions] = useState<LocationOptions>({});

    const dispatch = useDispatch();
    const navigator = useNavigator();

    if (
        dataSource.data !== undefined &&
        dataSource.data.locationsToRearrange.length &&
        dataSource.data.users.totalCount &&
        !Object.keys(options).length
    ) {
        const _options: LocationOptions = {};
        for (const location of dataSource.data.locationsToRearrange) {
            _options[location.id] = location.name;
        }
        setOptions(_options);
    }

    if (Object.keys(options).length && !destinationLocationId) {
        setDestinationLocationId(Number(Object.keys(options)[0]));
    }

    const [mutateDeleteWithMove] = useMoveExtensionsAndDeleteLocationMutation();

    const [mutateDelete] = useDeleteLocationMutation();

    async function submit() {
        setSubmitting(true);

        const response = destinationLocationId
            ? await mutateDeleteWithMove({variables: {sourceLocationId: locationId, destinationLocationId}})
            : await mutateDelete({variables: {id: locationId}});

        if (!response.data.deleteLocation.length) {
            dispatch(
                showNotification(
                    defineMessage({id: "location_deleted", defaultMessage: "Location deleted"}),
                    "success",
                ),
            );
            navigator.popSegment(2);
        } else {
            dispatch(
                showNotification(defineMessage({id: "unexpected_error", defaultMessage: "Unexpected error"}), "error"),
            );
        }

        setSubmitting(false);
    }

    function createOptions(): React.ReactNode {
        const elements = [];
        const optionsCopy = {...options};
        delete optionsCopy[locationId];

        for (const [key, label] of Object.entries(optionsCopy)) {
            elements.push(
                <MenuItem key={key} value={key}>
                    {label}
                </MenuItem>,
            );
        }

        return elements;
    }

    const showRelocateForm = Boolean(Object.keys(options).length);

    return (
        <div>
            <FormattedPageTitle desc={commonTranslations.delete} />
            {showRelocateForm && (
                <DialogContent>
                    <FormContainer style={{margin: "1em 0"}}>
                        <FormattedFormRow
                            required
                            label={defineMessage({
                                id: "delete_location.move_extensions",
                                defaultMessage: "Select location for existing users",
                            })}
                        >
                            <Select
                                variant="standard"
                                value={destinationLocationId}
                                margin="dense"
                                onChange={(event) => {
                                    setDestinationLocationId(Number(event.target.value));
                                }}
                            >
                                {createOptions()}
                            </Select>
                        </FormattedFormRow>
                    </FormContainer>
                </DialogContent>
            )}
            <FormattedFormRow>
                <FormActionButtons
                    mainButtonText={commonTranslations.delete}
                    mainAction={submit}
                    mainButtonColor="secondary"
                    secondaryAction={undefined}
                    secondaryButtonHidden
                    pending={submitting}
                />
            </FormattedFormRow>
        </div>
    );
};
